import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { validateEmail, validatePassword } from "../utils/formValidation";
import { Box, Button, IconButton, Link, useTheme } from "@mui/material";
import CustomLabel from "../components/CustomLabel";
import CustomTextField from "../components/CustomTextField";
import CustomCheckbox from "../components/CustomCheckbox";
import { login } from "../services/authService";
import { COLORS } from "../assets/theme";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useUser } from "../contexts/UserContext";

const Login: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refreshUser } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('api_token');
    if (user && token) {
      const from = (location.state as any)?.from || '/home';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const emailResult = validateEmail(email);
    const passwordResult = validatePassword(password);
    const newErrors: { email?: string; password?: string } = {};
    if (!emailResult.valid && emailResult.error)
      newErrors.email = emailResult.error;
    if (!passwordResult.valid && passwordResult.error)
      newErrors.password = passwordResult.error;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }
    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success("Login successful!", { position: "top-right" });
        if (result.user) {
          setUser(result.user);
        }
        await refreshUser();
        // Get the redirect path from location state, or default to home
        const from = (location.state as any)?.from || '/home';
        navigate(from, { replace: true });
      } else {
        toast.error("Login failed. Please check your credentials.", { position: "top-right" });
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Login failed. Please try again.";
      toast.error(errorMessage, { position: "top-right" });
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 400, md: 500 },
          maxWidth: "95vw",
          minHeight: { xs: 400, sm: 500, md: 600 },
          bgcolor: COLORS.WhiteBackground,
          borderRadius: 4,
          boxShadow: 3,
          p: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <CustomLabel
          text="Login"
          fontWeight={700}
          fontSize="2rem"
          color={COLORS.BlackText}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            borderBottom: `2px solid ${COLORS.BlackText}`,
            mb: 3,
            width: "80%",
          }}
        />
        <Box
          component="form"
          sx={{
            width: "100%",
          }}
          onSubmit={handleSubmit}
        >
          <CustomLabel
            text="Email"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type="email"
            value={email}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.email}
            helperText={errors.email}
          />
          <CustomLabel
            text="Password"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type={showPassword ? "text" : "password"}
            value={password}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 1.5 }}
            error={!!errors.password}
            helperText={errors.password}
            endIcon={
              <IconButton
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              mb: 2,
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                width: "100%",
              }}
            >
              <CustomCheckbox
                checked={remember}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRemember(e.target.checked)
                }
                ariaLabel="Remember me"
              />
              <CustomLabel
                text="Remember me"
                fontSize="13px"
                fontWeight={400}
                color={COLORS.BlackText}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                textAlign: "right",
                width: "100%",
              }}
            >
              <Link
                href="/forgot-password"
                underline="hover"
                sx={{
                  fontSize: "15px",
                  fontWeight: 500,
                  color: theme.palette.primary.main,
                  ml: 0,
                }}
              >
                Forgot password?
              </Link>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              borderRadius: 30,
              py: 1.5,
              fontWeight: 700,
              fontSize: "1.1rem",
              boxShadow: "0 2px 8px rgba(44, 130, 201, 0.12)",
              transition: "background 0.2s",
              background: loading
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              "&:hover": { background: theme.palette.primary.dark },
            }}
          >
            <CustomLabel
              text={loading ? "Logging in..." : "Login"}
              fontWeight={700}
              fontSize="1.1rem"
              color="#fff"
            />
          </Button>
        </Box>
        <CustomLabel
          text="Don't have an account?"
          fontWeight={400}
          fontSize="1rem"
          color={COLORS.BlackText}
          sx={{ textAlign: "center", mt: 3 }}
        />
        <Link
          href="/signup"
          underline="hover"
          sx={{
            fontWeight: 700,
            color: COLORS.BlackText,
            display: "block",
            textAlign: "center",
            mb: 2,
          }}
        >
          Sign Up
        </Link>
      </Box>
    </Box>
  );
};

export default Login;
